<template>
  <iCallDialog
    :open="open"
    :scrim="false"
    :max-width="400"
    :max-height="640"
    data-test="adjust-view-panel"
    @close="$emit('close')"
  >
    <iCallPanel
      title="Adjust view"
      description="Selection is saved for future meetings"
      close-label="Close adjust view"
      close-test-id="adjust-view-close"
      @close="$emit('close')"
    >
      <iColumn gap="none" width="fill" data-test="adjust-view-layouts">
        <iRow
          v-for="option in layoutOptions"
          :key="option.id"
          clickable
          align="between"
          vertical-align="middle"
          wrap="nowrap"
          width="fill"
          :data-test="`adjust-view-layout-${option.id}`"
          :data-selected="option.id === preferences.layout ? 'true' : 'false'"
          :style-overrides="{paddingTop: 'thin', paddingBottom: 'thin'}"
          @click="chooseLayout(option.id)"
        >
          <iColumn gap="none" width="fill">
            <iRow
              gap="small"
              vertical-align="middle"
              wrap="nowrap"
              width="hug"
            >
              <iRadioButton
                :model-value="preferences.layout"
                :value="option.id"
                name="adjust-view-layout"
                :label="option.label"
                :style-overrides="{
                  inputColor: 'interactiveSelected',
                  inputBorderColor: option.id === preferences.layout ? 'interactiveSelected' : 'darker',
                }"
              />
              <iIcon
                v-if="option.icon"
                :name="option.icon"
                size="small"
                disable-hover
              />
            </iRow>
            <iText
              v-if="option.description"
              variant="subtle"
              font-size="small"
              :data-test="`adjust-view-layout-${option.id}-description`"
            >
              {{ option.description }}
            </iText>
          </iColumn>
          <iLayoutPreview :variant="option.id" />
        </iRow>
      </iColumn>

      <iColumn gap="none" width="fill" :style-overrides="{paddingTop: 'small'}">
        <iText font-weight="dark">
          Tiles
        </iText>
        <iText variant="subtle" font-size="small">
          Maximum tiles to display, depending on window size.
        </iText>
        <iRow
          gap="standard"
          vertical-align="middle"
          wrap="nowrap"
          width="fill"
          data-test="adjust-view-tiles"
          :data-max-tiles="preferences.maxTiles"
          :data-locked="canAdjustTileSize ? 'false' : 'true'"
        >
          <iIcon name="material-symbols:grid-view-rounded" size="small" disable-hover />
          <iStepSlider
            :model-value="tileSliderIndex"
            :min="0"
            :max="tileStops.length - 1"
            aria-label="Maximum tiles"
            :disabled="!canAdjustTileSize"
            @update:model-value="chooseTileSliderIndex"
          />
          <iIcon name="material-symbols:apps" size="small" disable-hover />
        </iRow>
      </iColumn>

      <iRow
        align="between"
        vertical-align="middle"
        wrap="nowrap"
        width="fill"
        gap="standard"
      >
        <iText width="fill">
          Hide tiles without video
        </iText>
        <iSwitch
          :model-value="preferences.hideTilesWithoutVideo"
          aria-label="Hide tiles without video"
          data-test="adjust-view-hide-novideo"
          @update:model-value="$emit('change', {hideTilesWithoutVideo: Boolean($event)})"
        />
      </iRow>
    </iCallPanel>
  </iCallDialog>
</template>

<script>
import { CALL_SHORTCUTS, matchCallShortcut } from "%/apps/video-engine/call-shortcuts";
import { STAGE_TILE_CAPS, indexForTileCap, isTileSizeAdjustable, tileCapForIndex } from "%/apps/video-engine/view-preferences";
import { AUTO_LAYOUT_SUMMARY } from "%/apps/video-engine/stage-layout";

const LAYOUT_OPTIONS = [
  { id: "auto", label: "Auto (dynamic)", icon: "material-symbols:auto-awesome", description: AUTO_LAYOUT_SUMMARY },
  { id: "tiled", label: "Tiled", icon: "" },
  { id: "spotlight", label: "Spotlight", icon: "" },
  { id: "sidebar", label: "Sidebar", icon: "" },
];

export default {
  name: "AdjustViewPanel",
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    preferences: {
      type: Object,
      required: true,
    },
  },
  emits: ["change", "close"],
  computed: {
    layoutOptions() {
      return LAYOUT_OPTIONS;
    },
    tileStops() {
      return STAGE_TILE_CAPS;
    },
    tileSliderIndex() {
      return indexForTileCap(this.preferences.maxTiles);
    },
    canAdjustTileSize() {
      return isTileSizeAdjustable(this.preferences.layout);
    },
  },
  mounted() {
    window.addEventListener("keydown", this.closeOnDismiss);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.closeOnDismiss);
  },
  methods: {
    chooseLayout(layout) {
      if (layout === this.preferences.layout) {
        return;
      }
      this.$emit("change", { layout });
    },
    chooseTileSliderIndex(index) {
      const maxTiles = tileCapForIndex(Number(index));
      if (maxTiles === this.preferences.maxTiles) {
        return;
      }
      this.$emit("change", { maxTiles });
    },
    closeOnDismiss(event) {
      if (this.open && matchCallShortcut(event) === CALL_SHORTCUTS.dismiss) {
        this.$emit("close");
      }
    },
  },
};
</script>
